import React, { useState, useRef, useEffect } from 'react'
import { ChevronDown, Search } from 'lucide-react'
import type { ProductCategory } from '@/types/pos'
import { buildCategoryTree, filterCategoryTree, type CategoryTreeNode } from '@/lib/categoryTree'

interface CategoryTreeDropdownProps {
  categories: ProductCategory[]
  value: string
  onSelect: (categoryId: string) => void
  placeholder?: string
  className?: string
  /** Label for "all" option (e.g. "All Genres") */
  allLabel?: string
}

function TreeOption({
  node,
  depth,
  selectedId,
  onSelect,
}: {
  node: CategoryTreeNode
  depth: number
  selectedId: string
  onSelect: (id: string) => void
}) {
  const [open, setOpen] = useState(true)
  const id = node.category.id || (node.category as any)._id
  const hasChildren = node.children.length > 0
  const isSelected = selectedId === id

  return (
    <div className="flex flex-col">
      <div
        className="flex items-center gap-1 py-1.5 px-2 rounded-md hover:bg-muted/80 cursor-pointer group"
        style={{ paddingLeft: 8 + depth * 14 }}
        onClick={() => onSelect(id)}
      >
        {hasChildren ? (
          <button
            type="button"
            className="p-0.5 rounded hover:bg-muted"
            onClick={(e) => { e.stopPropagation(); setOpen((o) => !o) }}
            aria-label={open ? 'Collapse' : 'Expand'}
          >
            <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${open ? '' : '-rotate-90'}`} />
          </button>
        ) : (
          <span className="w-5" />
        )}
        <span className={`text-sm truncate flex-1 ${isSelected ? 'font-semibold text-primary' : ''}`}>
          {node.category.name}
        </span>
      </div>
      {hasChildren && open && (
        <div className="flex flex-col">
          {node.children.map((child) => (
            <TreeOption
              key={child.category.id || (child.category as any)._id}
              node={child}
              depth={depth + 1}
              selectedId={selectedId}
              onSelect={onSelect}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export const CategoryTreeDropdown: React.FC<CategoryTreeDropdownProps> = ({
  categories,
  value,
  onSelect,
  className = '',
  allLabel = 'All Genres',
}) => {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const ref = useRef<HTMLDivElement>(null)
  const fullTree = buildCategoryTree(categories)
  const tree = filterCategoryTree(fullTree, search)
  const selectedName = value === 'all' || !value
    ? allLabel
    : categories.find((c) => (c.id || (c as any)._id) === value)?.name ?? value

  useEffect(() => {
    if (!open) setSearch('')
  }, [open])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [open])

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 px-4 py-2 rounded-lg bg-muted hover:bg-muted/80 transition-colors text-left min-w-[140px]"
      >
        <span className="flex-1 truncate text-sm">{selectedName}</span>
        <ChevronDown className={`w-4 h-4 text-muted-foreground shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 z-50 min-w-[260px] max-h-[380px] overflow-hidden rounded-lg border border-border bg-background shadow-lg flex flex-col">
          <div className="p-2 border-b border-border shrink-0">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search genres..."
                className="w-full pl-8 pr-3 py-2 text-sm bg-muted/50 border border-border rounded-md focus:ring-2 focus:ring-primary focus:outline-none"
              />
            </div>
          </div>
          <div className="overflow-y-auto py-2 max-h-[300px]">
            <div
              className="py-1.5 px-2 rounded-md hover:bg-muted/80 cursor-pointer mx-1"
              onClick={() => { onSelect('all'); setOpen(false) }}
            >
              <span className={`text-sm font-medium ${value === 'all' || !value ? 'text-primary font-semibold' : ''}`}>
                {allLabel}
              </span>
            </div>
            <div className="border-t border-border my-1" />
            {tree.length === 0 ? (
              <p className="text-sm text-muted-foreground px-3 py-2">No genres match &quot;{search}&quot;</p>
            ) : (
              tree.map((node) => (
                <TreeOption
                  key={node.category.id || (node.category as any)._id}
                  node={node}
                  depth={0}
                  selectedId={value === 'all' ? '' : value}
                  onSelect={(id) => { onSelect(id); setOpen(false) }}
                />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}
